import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createAuthenticatedApiInstance } from 'src/utils/api';
import { groupDataByReason, addStartAndEndDates, getFullSetOfDates, getValues } from 'src/modules/community/communityMetrics/moderation/moderationUtil';
import { Moderation } from 'src/modules/community/communityMetrics/types';

export const initialState: Moderation = {
  endDate: null,
  contentReportReasons: {
    dates: [],
    reasons: [],
    status: 'idle',
    values: [],
  },
};

export const moderationSlice = createSlice({
  name: 'moderation',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchContentReportReasons.pending, (state) => {
        state.contentReportReasons.status = 'loading';
      })
      .addCase(fetchContentReportReasons.fulfilled, (state, action) => {
        const startDate = action.payload.meta?.startDate;
        const endDate = action.payload.meta?.endDate;
        state.endDate = endDate;
        state.contentReportReasons.status = 'succeeded';

        // Parsing data required because data returned by BE has no specific ordering and might not have 12 months of data
        const chartData = action.payload.rows as [string, number, string][] || [];
        const dataGroupedByReason = groupDataByReason(chartData);
        const reasons = dataGroupedByReason.map(reason => reason.reason);
        state.contentReportReasons.reasons = reasons;

        // Dates parsing
        const existingDates = chartData.map(array => {
          const date = array[0].split('-'); // to remove the day
          return new Date(`${date[0]}-${date[1]}`);
        });
        // Sort dates chronologically
        const sortedDates = existingDates.sort((a, b) => a.getTime() - b.getTime()).map(date => date.toISOString());
        const filteredDates = sortedDates.reduce((acc, current) => { // remove duplicates
          if (!acc.find(date => date === current)) acc.push(current);
          return acc;
        }, []);
        // Complete set of data from BE would have 72 results (6 reasons x 12 months).
        // If we don't have 72 results, we add the missing data
        if (chartData.length < 72 && startDate && endDate) {
          const completedDates = addStartAndEndDates(filteredDates, startDate, endDate);
          const newDates = getFullSetOfDates(completedDates);
          state.contentReportReasons.dates = newDates;
          const completeValues = getValues(dataGroupedByReason, newDates);
          state.contentReportReasons.values = completeValues;
        } else {
          state.contentReportReasons.dates = filteredDates;
          const completeValues = getValues(dataGroupedByReason, filteredDates);
          state.contentReportReasons.values = completeValues;
        }
      });
  },
});

export default moderationSlice.reducer;

export const fetchContentReportReasons = createAsyncThunk('moderation/fetchContentReportReasons', async (slug: string, thunkApi) => {
  const api = createAuthenticatedApiInstance(thunkApi.getState);
  try {
    const { data } = await api.get(`private/communities/${slug}/metrics/ReportOfAbuseReasonMonthly`);
    return data;
  } catch (err) {
    // A failed request or error in a thunk will never return a rejected promise.
    // As we want to log in the errors/reason/status of the failed request to Sentry,
    // we need to return a new value using the thunkAPI.rejectWithValue utility.
    return thunkApi.rejectWithValue(err);
  }
});
